import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Breadcrumbs,
    Link as MuiLink,
    CircularProgress,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getRunSheet, updateMasterUnitStatus, printRunSheet, RunSheet } from '../../services/runSheetApi';
import GenericModal from '../../components/GenericModal';
import useToast from '../../hooks/useToast';
import Toast from '../../components/Toast';

const RunSheetView = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [runSheet, setRunSheet] = useState<RunSheet | null>(null);
    const [loading, setLoading] = useState(true);
    const { toast, showSuccess, showError, hideToast } = useToast();
    const [confirmModal, setConfirmModal] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchRunSheet();
    }, [id]);

    const fetchRunSheet = async () => {
        try {
            setLoading(true);
            const response = await getRunSheet(Number(id));
            setRunSheet(response.data);
        } catch (error: any) {
            showError(error.message || 'Failed to load run sheet');
            navigate('/run-sheets');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = (masterUnitId: number, statusId: number) => {
        setConfirmModal({
            open: true,
            title: 'Update Master Unit Status',
            message: 'Are you sure you want to update the status of this master unit?',
            onConfirm: async () => {
                setSubmitting(true);
                try {
                    await updateMasterUnitStatus(Number(id), masterUnitId, { status_id: statusId });
                    setConfirmModal(prev => ({ ...prev, open: false }));
                    showSuccess('Master unit status updated successfully!');
                    fetchRunSheet();
                } catch (error: any) {
                    showError(error.message || 'Failed to update master unit status');
                } finally {
                    setSubmitting(false);
                }
            },
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!runSheet) {
        return null;
    }

    const isCheckedIn = runSheet.status_id === 22;

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 3 }}>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/dashboard')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Home
                </MuiLink>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/run-sheets')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Run Sheets
                </MuiLink>
                <Typography color="text.primary">{runSheet.unique_id}</Typography>
            </Breadcrumbs>

            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Run Sheet: {runSheet.unique_id}
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<PrintIcon />}
                            onClick={() => printRunSheet(Number(id))}
                        >
                            Print
                        </Button>
                    </Box>

                    {/* Basic Information */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ bgcolor: theme.palette.action.hover, p: 1, mb: 2 }}>
                            Basic Information
                        </Typography>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, width: '200px' }}>Rider:</TableCell>
                                    <TableCell>{runSheet.rider?.name}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Created At:</TableCell>
                                    <TableCell>{new Date(runSheet.created_at).toLocaleString()}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Status:</TableCell>
                                    <TableCell>
                                        <Chip label={runSheet.status.name} color="primary" size="small" />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Paper>

                    {/* Master Units Table */}
                    <Typography variant="h6" gutterBottom sx={{ bgcolor: theme.palette.action.hover, p: 1, mb: 2 }}>
                        Master Units ({runSheet.masterunits?.length || 0})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Master Unit #</TableCell>
                                    <TableCell>Consignments</TableCell>
                                    <TableCell>Client Name</TableCell>
                                    <TableCell>Status</TableCell>
                                    {isCheckedIn && <TableCell>Actions</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {runSheet.masterunits && runSheet.masterunits.length > 0 ? (
                                    runSheet.masterunits.map((mu) => (
                                        <TableRow key={mu.id}>
                                            <TableCell>
                                                <MuiLink
                                                    component="button"
                                                    onClick={() => navigate(`/master-units/${mu.id}`)}
                                                    sx={{ textDecoration: 'none' }}
                                                >
                                                    {mu.mu_number}
                                                </MuiLink>
                                            </TableCell>
                                            <TableCell>{mu.consignments?.length || 0}</TableCell>
                                            <TableCell>{mu.client?.name || '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={mu.status.name}
                                                    color="info"
                                                    size="small"
                                                />
                                            </TableCell>
                                            {isCheckedIn && (
                                                <TableCell>
                                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                                        <Select
                                                            value={mu.status_id}
                                                            onChange={(e) => handleStatusChange(mu.id, e.target.value as number)}
                                                            displayEmpty
                                                        >
                                                            <MenuItem value={mu.status_id}>{mu.status.name}</MenuItem>
                                                            <MenuItem value={18}>Returned to Customer</MenuItem>
                                                            <MenuItem value={14}>Arrived at Origin</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={isCheckedIn ? 5 : 4} align="center">
                                            No master units found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/run-sheets')}
                        >
                            Back
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <GenericModal
                open={confirmModal.open}
                onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                title={confirmModal.title}
                maxWidth="xs"
                actions={
                    <>
                        <Button
                            onClick={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                            disabled={submitting}
                            sx={{ color: theme.palette.text.secondary, borderRadius: 3 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={confirmModal.onConfirm}
                            disabled={submitting}
                            sx={{ borderRadius: 3 }}
                        >
                            {submitting ? 'Processing...' : 'Yes, Proceed'}
                        </Button>
                    </>
                }
            >
                <Typography variant="body1" color="text.primary">
                    {confirmModal.message}
                </Typography>
            </GenericModal>

            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default RunSheetView;



